import { c as _c } from "react/compiler-runtime";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { LifeBuoy, MessageSquare, Paperclip, Pencil, Plus, Trash2, X } from "lucide-react";
import { api } from "@/api";
import type { FaqArticle, FaqAudience, SupportAttachment, SupportTicket, SupportTicketStatus, UpdateFaqArticleRequest } from "@/api";
import { useCreateFaqArticle, useDeleteFaqArticle, useFaqArticles, useReplyTicket, useSetTicketStatus, useSupportTicket, useSupportTickets, useUpdateFaqArticle } from "@/api/hooks/use-support";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { DataTablePagination, SortableHead } from "@/components/data-table";
import { FilterChips, type FilterChip } from "@/components/filter-chips";
import { Segmented } from "@/components/segmented";
import { Badge } from "@osmix/ui/badge";
import { Button } from "@osmix/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@osmix/ui/dialog";
import { Input } from "@osmix/ui/input";
import { Label } from "@osmix/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useDataTable } from "@/lib/use-data-table";
import { formatDateTime } from "@/lib/utils";

// Порядок статусов обращения по жизненному циклу — им же задаём чипы-фильтры.
const TICKET_STATUSES: SupportTicketStatus[] = ["open", "in_progress", "resolved", "closed"];
const STATUS_VARIANT: Record<SupportTicketStatus, "default" | "secondary" | "destructive" | "outline"> = {
  open: "secondary",
  in_progress: "default",
  resolved: "outline",
  closed: "outline"
};
type Tab = "tickets" | "faq";
export function SupportPage() {
  const $ = _c(27);
  const {
    t
  } = useTranslation();
  const [tab, setTab] = useState("tickets");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <LifeBuoy className="size-6" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== t) {
    t1 = t("nav.support");
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <h1 data-tour="support-intro" className="flex items-center gap-2 text-2xl font-bold">{t0}{t1}</h1>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t) {
    t3 = t("support.tabs.tickets");
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t3) {
    t4 = {
      value: "tickets",
      label: t3,
      icon: MessageSquare
    };
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t) {
    t5 = t("support.tabs.faq");
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t5) {
    t6 = {
      value: "faq",
      label: t5,
      icon: LifeBuoy
    };
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t4 || $[14] !== t6) {
    t7 = [t4, t6];
    $[13] = t4;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t7 || $[17] !== tab) {
    t8 = <div data-tour="support-tabs"><Segmented value={tab} onChange={setTab} options={t7} /></div>;
    $[16] = t7;
    $[17] = tab;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t2 || $[20] !== t8) {
    t9 = <div className="flex flex-wrap items-center justify-between gap-2">{t2}{t8}</div>;
    $[19] = t2;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== tab) {
    t10 = tab === "tickets" ? <TicketsTab /> : <FaqTab />;
    $[22] = tab;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t9) {
    t11 = <div className="flex flex-col gap-4">{t9}{t10}</div>;
    $[24] = t10;
    $[25] = t9;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  return t11;
}

// ── Обращения ──────────────────────────────────────────────────────────────

type TicketSortKey = "number" | "subject" | "user" | "category" | "messages" | "updated" | "status";
function TicketsTab() {
  const {
    t
  } = useTranslation();
  const {
    data,
    error: loadError
  } = useSupportTickets();
  const error = loadError ? loadError.message : null;
  const [status, setStatus] = useState<SupportTicketStatus | "">("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [who, setWho] = useState<"" | "seller" | "buyer">("");
  const all = data ?? [];
  const counts = useMemo(() => {
    const map = new Map<string, number>();
    for (const ticket of all) map.set(ticket.status, (map.get(ticket.status) ?? 0) + 1);
    return map;
  }, [all]);
  const chips: FilterChip<SupportTicketStatus>[] = TICKET_STATUSES.map(value => ({
    value,
    label: t(`support.status.${value}`),
    count: counts.get(value) ?? 0,
    tone: value === "open" ? "warning" : "default"
  }));
  const q = search.trim().toLowerCase();
  const filtered = all.filter(ticket_0 => {
    if (status && ticket_0.status !== status) return false;
    if (who && ticket_0.userRole !== who) return false;
    if (!q) return true;
    return [String(ticket_0.number), ticket_0.subject, ticket_0.userName ?? "", ticket_0.userContact ?? "", ticket_0.category].join(" ").toLowerCase().includes(q);
  });
  const table = useDataTable<SupportTicket, TicketSortKey>({
    rows: filtered,
    pageSize: 25,
    initialSort: {
      key: "updated",
      dir: "desc"
    },
    accessors: {
      number: ticket_1 => ticket_1.number,
      subject: ticket_2 => ticket_2.subject,
      user: ticket_3 => ticket_3.userName ?? ticket_3.userId,
      category: ticket_4 => ticket_4.category,
      messages: ticket_5 => ticket_5.messageCount,
      updated: ticket_6 => ticket_6.lastMessageAt,
      status: ticket_7 => ticket_7.status
    }
  });
  if (error) return <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
				{error}
			</div>;
  if (data === undefined) return <p className="text-muted-foreground">{t("support.tickets.loading")}</p>;
  return <div className="flex flex-col gap-4">
			<div data-tour="support-filters">
			<FilterChips value={status} chips={chips} onChange={next => {
        table.setPage(1);
        setStatus(next);
      }} allLabel={t("support.filter.all")} allCount={all.length} />
			</div>

			<div className="flex flex-wrap items-center gap-2">
				<Input value={search} onChange={event => {
        table.setPage(1);
        setSearch(event.target.value);
      }} placeholder={t("support.tickets.searchPlaceholder")} className="h-9 w-full sm:max-w-sm" />
				<Segmented<"" | "seller" | "buyer"> value={who} onChange={next_0 => {
        table.setPage(1);
        setWho(next_0);
      }} options={[{
        value: "",
        label: t("support.who.all")
      }, {
        value: "seller",
        label: t("support.who.seller")
      }, {
        value: "buyer",
        label: t("support.who.buyer")
      }]} />
			</div>

			{all.length === 0 ? <p className="text-muted-foreground">{t("support.tickets.empty")}</p> : filtered.length === 0 ? <p className="text-muted-foreground">{t("support.tickets.filterEmpty")}</p> : <div className="flex flex-col gap-4">
					<Table>
						<TableHeader>
							<TableRow>
								<SortableHead label={t("support.col.number")} sortKey="number" sort={table.sort} onToggle={table.toggleSort} />
								<SortableHead label={t("support.col.subject")} sortKey="subject" sort={table.sort} onToggle={table.toggleSort} />
								<SortableHead label={t("support.col.user")} sortKey="user" sort={table.sort} onToggle={table.toggleSort} />
								<SortableHead label={t("support.col.category")} sortKey="category" sort={table.sort} onToggle={table.toggleSort} />
								<SortableHead label={t("support.col.messages")} sortKey="messages" sort={table.sort} onToggle={table.toggleSort} align="right" className="text-right" />
								<SortableHead label={t("support.col.updated")} sortKey="updated" sort={table.sort} onToggle={table.toggleSort} />
								<SortableHead label={t("support.col.status")} sortKey="status" sort={table.sort} onToggle={table.toggleSort} />
							</TableRow>
						</TableHeader>
						<TableBody>
							{table.pageRows.map(ticket_8 => <TableRow key={ticket_8.id} className="cursor-pointer" onClick={() => setSelectedId(ticket_8.id)}>
									<TableCell className="tabular-nums text-muted-foreground">№{ticket_8.number}</TableCell>
									<TableCell className="max-w-[22rem] truncate font-medium">{ticket_8.subject}</TableCell>
									<TableCell>
										<span className="flex flex-wrap items-center gap-1.5">
											{ticket_8.userName ?? ticket_8.userId}
											{ticket_8.userRole && <Badge variant={ticket_8.userRole === "seller" ? "default" : "outline"}>
													{t(`support.who.${ticket_8.userRole}`)}
												</Badge>}
										</span>
									</TableCell>
									<TableCell className="text-muted-foreground">{ticket_8.category}</TableCell>
									<TableCell className="text-right tabular-nums">{ticket_8.messageCount}</TableCell>
									<TableCell className="whitespace-nowrap text-muted-foreground">{formatDateTime(ticket_8.lastMessageAt)}</TableCell>
									<TableCell>
										<Badge variant={STATUS_VARIANT[ticket_8.status]}>{t(`support.status.${ticket_8.status}`)}</Badge>
									</TableCell>
								</TableRow>)}
						</TableBody>
					</Table>
					<DataTablePagination table={table} />
				</div>}

			<TicketDialog ticketId={selectedId} onClose={() => setSelectedId(null)} />
		</div>;
}
function TicketDialog({
  ticketId,
  onClose
}: {
  ticketId: string | null;
  onClose: () => void;
}) {
  const {
    t
  } = useTranslation();
  const {
    data: ticket,
    error: loadError
  } = useSupportTicket(ticketId);
  const reply = useReplyTicket();
  const setStatus = useSetTicketStatus();
  const [body, setBody] = useState("");
  const [files, setFiles] = useState<SupportAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const busy = reply.isPending || setStatus.isPending || uploading;
  const actionError = reply.error || setStatus.error || loadError;
  const attach = async (list: FileList | null) => {
    if (!list || list.length === 0) return;
    setUploading(true);
    setUploadError(null);
    try {
      const uploaded: SupportAttachment[] = [];
      for (const file of Array.from(list).slice(0, 10 - files.length)) {
        uploaded.push(await api.support.uploadAttachment(file));
      }
      setFiles(prev => [...prev, ...uploaded]);
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
    }
  };
  const close = () => {
    setBody("");
    setFiles([]);
    setUploadError(null);
    reply.reset();
    setStatus.reset();
    onClose();
  };
  const submitReply = () => {
    if (!ticket || !body.trim() && files.length === 0) return;
    reply.mutate({
      id: ticket.id,
      input: {
        body: body.trim(),
        attachments: files
      }
    }, {
      onSuccess: () => {
        setBody("");
        setFiles([]);
      }
    });
  };
  if (!ticketId) return null;
  return <Dialog open onOpenChange={open => !open && close()}>
			<DialogContent className="flex max-h-[85vh] flex-col overflow-hidden sm:max-w-2xl">
				<DialogHeader>
					<DialogTitle className="flex flex-wrap items-center gap-2 pr-6">
						{ticket && <span className="text-muted-foreground tabular-nums">№{ticket.number}</span>}
						<span className="truncate">{ticket?.subject ?? t("support.tickets.loading")}</span>
						{ticket?.userRole && <Badge variant={ticket.userRole === "seller" ? "default" : "outline"}>{t(`support.who.${ticket.userRole}`)}</Badge>}
						{ticket && <Badge variant={STATUS_VARIANT[ticket.status]}>{t(`support.status.${ticket.status}`)}</Badge>}
					</DialogTitle>
				</DialogHeader>

				{ticket ? <div className="flex min-h-0 flex-col gap-4">
						<div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
							<span>
								{t("support.ticket.from")}:{" "}
								{ticket.userId ? <Link to={`/users/${ticket.userId}`} className="text-primary hover:underline">
										{ticket.userName ?? ticket.userId}
									</Link> : ticket.userName ?? "—"}
							</span>
							{ticket.userContact && <span>{ticket.userContact}</span>}
							<span>{t("support.ticket.category")}: {ticket.category}</span>
							<span>{t("support.ticket.created")}: {formatDateTime(ticket.createdAt)}</span>
						</div>

						{/* Тред: сообщения пользователя слева, поддержки справа. */}
						<div className="flex max-h-[40vh] flex-col gap-3 overflow-y-auto rounded-md border bg-muted/30 p-3">
							{ticket.messages.map(message => {
            const isSupport = message.authorRole === "support";
            return <div key={message.id} className={isSupport ? "flex justify-end" : "flex justify-start"}>
										<div className={isSupport ? "max-w-[85%] rounded-lg rounded-br-sm bg-primary px-3 py-2 text-sm text-primary-foreground" : "max-w-[85%] rounded-lg rounded-bl-sm border bg-background px-3 py-2 text-sm"}>
											<p className="mb-0.5 text-xs opacity-70">
												{message.authorName ?? (isSupport ? t("support.ticket.support") : t("support.ticket.user"))}
												{" · "}
												{formatDateTime(message.createdAt)}
											</p>
											{message.body && <p className="whitespace-pre-wrap">{message.body}</p>}
											{message.attachments.length > 0 && <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="ticket-attachments">
													{message.attachments.map(file_0 => <a key={file_0.url} href={file_0.url} target="_blank" rel="noreferrer" className={isSupport ? "underline" : "text-primary underline"}>
															{file_0.kind === "image" ? <img src={file_0.url} alt={file_0.name} className="size-16 rounded object-cover" /> : <span className="inline-flex items-center gap-1 text-xs">
																	<Paperclip className="size-3" />
																	{file_0.name || t("support.ticket.file")}
																</span>}
														</a>)}
												</div>}
										</div>
									</div>;
          })}
						</div>

						{actionError && <p className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-sm text-destructive">
								{actionError.message}
							</p>}

						{/* Ответ поддержки. */}
						<div className="flex flex-col gap-2">
							<Label htmlFor="support-reply">{t("support.ticket.replyLabel")}</Label>
							<textarea id="support-reply" className="min-h-20 rounded-md border border-input bg-transparent px-3 py-2 text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50" value={body} onChange={event => setBody(event.target.value)} placeholder={t("support.ticket.replyPlaceholder")} disabled={busy} />
							{files.length > 0 && <div className="flex flex-wrap gap-1.5">
									{files.map(file_1 => <Badge key={file_1.url} variant="secondary" className="gap-1">
											<Paperclip className="size-3" />
											{file_1.name}
											<button type="button" aria-label={t("support.ticket.removeFile", {
                name: file_1.name
              })} onClick={() => setFiles(prev_0 => prev_0.filter(f => f.url !== file_1.url))}>
												<X className="size-3" />
											</button>
										</Badge>)}
								</div>}
							{uploadError && <p className="text-xs text-destructive">{uploadError}</p>}
							<div className="flex flex-wrap items-center justify-between gap-2">
								<div className="flex flex-wrap items-center gap-2">
									<Button onClick={submitReply} disabled={busy || !body.trim() && files.length === 0}>
										{t("support.ticket.send")}
									</Button>
									<label className="inline-flex cursor-pointer items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
										<Paperclip className="size-4" />
										{uploading ? t("support.ticket.uploading") : t("support.ticket.attach")}
										<input type="file" multiple accept="image/jpeg,image/png,image/webp,image/gif,image/heic,application/pdf" className="sr-only" data-testid="ticket-file-input" disabled={busy} onChange={event_0 => {
                  void attach(event_0.target.files);
                  event_0.target.value = "";
                }} />
									</label>
								</div>
								{/* Смена статуса: доступные переходы зависят от текущего. */}
								<div className="flex flex-wrap gap-2">
									{ticket.status !== "in_progress" && ticket.status !== "closed" && <Button variant="outline" onClick={() => setStatus.mutate({
                id: ticket.id,
                status: "in_progress"
              })} disabled={busy}>
											{t("support.action.in_progress")}
										</Button>}
									{ticket.status !== "resolved" && ticket.status !== "closed" && <Button variant="outline" onClick={() => setStatus.mutate({
                id: ticket.id,
                status: "resolved"
              })} disabled={busy}>
											{t("support.action.resolved")}
										</Button>}
									{ticket.status !== "closed" && <Button variant="outline" onClick={() => setStatus.mutate({
                id: ticket.id,
                status: "closed"
              })} disabled={busy}>
											{t("support.action.closed")}
										</Button>}
								</div>
							</div>
						</div>
					</div> : loadError ? <p className="text-sm text-destructive">{loadError.message}</p> : <p className="text-muted-foreground">{t("support.tickets.loading")}</p>}
			</DialogContent>
		</Dialog>;
}

// ── База знаний (FAQ) ────────────────────────────────────────────────────────

function FaqTab() {
  const {
    t
  } = useTranslation();
  const {
    data,
    error: loadError
  } = useFaqArticles();
  const del = useDeleteFaqArticle();
  const [editing, setEditing] = useState<FaqArticle | null | "new">(null);
  const [pendingDelete, setPendingDelete] = useState<FaqArticle | null>(null);
  const [audience, setAudience] = useState<"" | FaqAudience>("");
  const error = loadError ? loadError.message : null;
  // «Для покупателей» показывает и общие статьи — ровно то, что увидит витрина.
  const all = (data ?? []).filter(article => {
    const a = article.audience ?? "all";
    return !audience || a === audience || audience !== "all" && a === "all";
  });

  // Группируем по категории для отображения (админ-эндпоинт отдаёт плоский
  // список; порядок внутри категории — по sortOrder, задаётся хуком).
  const groups = useMemo(() => {
    const map = new Map<string, FaqArticle[]>();
    for (const article_0 of all) {
      const list = map.get(article_0.category) ?? [];
      list.push(article_0);
      map.set(article_0.category, list);
    }
    return [...map.entries()];
  }, [all]);
  if (error) return <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
				{error}
			</div>;
  if (data === undefined) return <p className="text-muted-foreground">{t("support.faq.loading")}</p>;
  return <div className="flex flex-col gap-4">
			<div className="flex flex-wrap items-center justify-between gap-2">
				<Segmented<"" | FaqAudience> value={audience} onChange={setAudience} options={[{
        value: "",
        label: t("support.faq.audienceFilter.all")
      }, {
        value: "buyers",
        label: t("support.faq.audienceFilter.buyers")
      }, {
        value: "sellers",
        label: t("support.faq.audienceFilter.sellers")
      }]} />
				<Button onClick={() => setEditing("new")}>
					<Plus className="size-4" />
					{t("support.faq.add")}
				</Button>
			</div>

			{all.length === 0 ? <p className="text-muted-foreground">{t("support.faq.empty")}</p> : <div className="flex flex-col gap-6">
					{groups.map(([category, articles]) => <section key={category} className="flex flex-col gap-2">
							<h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
								{category} · {articles.length}
							</h2>
							<div className="flex flex-col gap-2">
								{articles.map(article_1 => <div key={article_1.id} className="flex items-start justify-between gap-3 rounded-md border p-3">
										<div className="min-w-0">
											<p className="flex flex-wrap items-center gap-2 font-medium">
												{article_1.question}
												{article_1.published ? <Badge variant="secondary">{t("support.faq.published")}</Badge> : <Badge variant="outline">{t("support.faq.draft")}</Badge>}
												<Badge variant="outline">{t(`support.faq.audience.${article_1.audience ?? "all"}`)}</Badge>
												<span className="text-xs font-normal text-muted-foreground">#{article_1.sortOrder}</span>
											</p>
											<p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">{article_1.answer}</p>
										</div>
										<div className="flex shrink-0 gap-1">
											<Button variant="ghost" size="icon-sm" aria-label={t("support.faq.edit")} onClick={() => setEditing(article_1)}>
												<Pencil className="size-4" />
											</Button>
											<Button variant="ghost" size="icon-sm" aria-label={t("support.faq.delete")} onClick={() => setPendingDelete(article_1)}>
												<Trash2 className="size-4 text-destructive" />
											</Button>
										</div>
									</div>)}
							</div>
						</section>)}
				</div>}

			{editing && <FaqFormDialog article={editing === "new" ? null : editing} onClose={() => setEditing(null)} />}

			<ConfirmDialog open={pendingDelete !== null} title={t("support.faq.deleteTitle")} description={pendingDelete?.question} confirmVariant="destructive" busy={del.isPending} onConfirm={() => {
      if (!pendingDelete) return;
      del.mutate(pendingDelete.id, {
        onSuccess: () => setPendingDelete(null)
      });
    }} onClose={() => setPendingDelete(null)} />
		</div>;
}
function FaqFormDialog(t0) {
  const $ = _c(141);
  const {
    article,
    onClose
  } = t0;
  const {
    t
  } = useTranslation();
  const create = useCreateFaqArticle();
  const update = useUpdateFaqArticle();
  const [category, setCategory] = useState(article?.category ?? "");
  const [question, setQuestion] = useState(article?.question ?? "");
  const [answer, setAnswer] = useState(article?.answer ?? "");
  const [sortOrder, setSortOrder] = useState(String(article?.sortOrder ?? 0));
  const [published, setPublished] = useState(article?.published ?? false);
  const [audience, setAudience] = useState(article?.audience ?? "all");
  const busy = create.isPending || update.isPending;
  const actionError = create.error || update.error;
  let t1;
  if ($[0] !== answer || $[1] !== category || $[2] !== question) {
    t1 = category.trim() && question.trim() && answer.trim();
    $[0] = answer;
    $[1] = category;
    $[2] = question;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const valid = t1;
  let t2;
  if ($[4] !== answer || $[5] !== article || $[6] !== audience || $[7] !== category || $[8] !== create || $[9] !== onClose || $[10] !== published || $[11] !== question || $[12] !== sortOrder || $[13] !== update || $[14] !== valid) {
    t2 = () => {
      if (!valid) {
        return;
      }
      const order = Number.parseInt(sortOrder, 10);
      if (article) {
        const patch = {
          category: category.trim(),
          question: question.trim(),
          answer: answer.trim(),
          sortOrder: Number.isFinite(order) ? order : 0,
          published,
          audience
        };
        update.mutate({
          id: article.id,
          input: patch
        }, {
          onSuccess: onClose
        });
      } else {
        create.mutate({
          category: category.trim(),
          question: question.trim(),
          answer: answer.trim(),
          sortOrder: Number.isFinite(order) ? order : 0,
          published,
          audience
        }, {
          onSuccess: onClose
        });
      }
    };
    $[4] = answer;
    $[5] = article;
    $[6] = audience;
    $[7] = category;
    $[8] = create;
    $[9] = onClose;
    $[10] = published;
    $[11] = question;
    $[12] = sortOrder;
    $[13] = update;
    $[14] = valid;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  const submit = t2;
  let t3;
  if ($[16] !== onClose) {
    t3 = open => !open && onClose();
    $[16] = onClose;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  let t4;
  if ($[18] !== article || $[19] !== t) {
    t4 = article ? t("support.faq.editTitle") : t("support.faq.addTitle");
    $[18] = article;
    $[19] = t;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  let t5;
  if ($[21] !== t4) {
    t5 = <DialogHeader><DialogTitle>{t4}</DialogTitle></DialogHeader>;
    $[21] = t4;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  let t6;
  if ($[23] !== t) {
    t6 = t("support.faq.field.category");
    $[23] = t;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  let t7;
  if ($[25] !== t6) {
    t7 = <Label htmlFor="faq-category">{t6}</Label>;
    $[25] = t6;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = event => setCategory(event.target.value);
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] !== t) {
    t9 = t("support.faq.field.categoryPlaceholder");
    $[28] = t;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== category || $[31] !== t9) {
    t10 = <Input id="faq-category" value={category} onChange={t8} placeholder={t9} />;
    $[30] = category;
    $[31] = t9;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== t10 || $[34] !== t7) {
    t11 = <div className="flex flex-col gap-2">{t7}{t10}</div>;
    $[33] = t10;
    $[34] = t7;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== t) {
    t12 = t("support.faq.field.audience");
    $[36] = t;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[38] !== t12) {
    t13 = <Label htmlFor="faq-audience">{t12}</Label>;
    $[38] = t12;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = event_0 => setAudience(event_0.target.value as FaqAudience);
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== t) {
    t15 = t("support.faq.audience.all");
    $[41] = t;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== t15) {
    t16 = <option value="all">{t15}</option>;
    $[43] = t15;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== t) {
    t17 = t("support.faq.audience.buyers");
    $[45] = t;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] !== t17) {
    t18 = <option value="buyers">{t17}</option>;
    $[47] = t17;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== t) {
    t19 = t("support.faq.audience.sellers");
    $[49] = t;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  let t20;
  if ($[51] !== t19) {
    t20 = <option value="sellers">{t19}</option>;
    $[51] = t19;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  let t21;
  if ($[53] !== audience || $[54] !== t16 || $[55] !== t18 || $[56] !== t20) {
    t21 = <select id="faq-audience" className="h-9 rounded-md border border-input bg-transparent px-2 text-sm" value={audience} onChange={t14}>{t16}{t18}{t20}</select>;
    $[53] = audience;
    $[54] = t16;
    $[55] = t18;
    $[56] = t20;
    $[57] = t21;
  } else {
    t21 = $[57];
  }
  let t22;
  if ($[58] !== t) {
    t22 = t("support.faq.field.audienceHint");
    $[58] = t;
    $[59] = t22;
  } else {
    t22 = $[59];
  }
  let t23;
  if ($[60] !== t22) {
    t23 = <p className="text-xs text-muted-foreground">{t22}</p>;
    $[60] = t22;
    $[61] = t23;
  } else {
    t23 = $[61];
  }
  let t24;
  if ($[62] !== t13 || $[63] !== t21 || $[64] !== t23) {
    t24 = <div className="flex flex-col gap-2">{t13}{t21}{t23}</div>;
    $[62] = t13;
    $[63] = t21;
    $[64] = t23;
    $[65] = t24;
  } else {
    t24 = $[65];
  }
  let t25;
  if ($[66] !== t) {
    t25 = t("support.faq.field.question");
    $[66] = t;
    $[67] = t25;
  } else {
    t25 = $[67];
  }
  let t26;
  if ($[68] !== t25) {
    t26 = <Label htmlFor="faq-question">{t25}</Label>;
    $[68] = t25;
    $[69] = t26;
  } else {
    t26 = $[69];
  }
  let t27;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = event_1 => setQuestion(event_1.target.value);
    $[70] = t27;
  } else {
    t27 = $[70];
  }
  let t28;
  if ($[71] !== question) {
    t28 = <Input id="faq-question" value={question} onChange={t27} />;
    $[71] = question;
    $[72] = t28;
  } else {
    t28 = $[72];
  }
  let t29;
  if ($[73] !== t26 || $[74] !== t28) {
    t29 = <div className="flex flex-col gap-2">{t26}{t28}</div>;
    $[73] = t26;
    $[74] = t28;
    $[75] = t29;
  } else {
    t29 = $[75];
  }
  let t30;
  if ($[76] !== t) {
    t30 = t("support.faq.field.answer");
    $[76] = t;
    $[77] = t30;
  } else {
    t30 = $[77];
  }
  let t31;
  if ($[78] !== t30) {
    t31 = <Label htmlFor="faq-answer">{t30}</Label>;
    $[78] = t30;
    $[79] = t31;
  } else {
    t31 = $[79];
  }
  let t32;
  if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = event_2 => setAnswer(event_2.target.value);
    $[80] = t32;
  } else {
    t32 = $[80];
  }
  let t33;
  if ($[81] !== answer) {
    t33 = <textarea id="faq-answer" className="min-h-28 rounded-md border border-input bg-transparent px-3 py-2 text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50" value={answer} onChange={t32} />;
    $[81] = answer;
    $[82] = t33;
  } else {
    t33 = $[82];
  }
  let t34;
  if ($[83] !== t31 || $[84] !== t33) {
    t34 = <div className="flex flex-col gap-2">{t31}{t33}</div>;
    $[83] = t31;
    $[84] = t33;
    $[85] = t34;
  } else {
    t34 = $[85];
  }
  let t35;
  if ($[86] !== t) {
    t35 = t("support.faq.field.sortOrder");
    $[86] = t;
    $[87] = t35;
  } else {
    t35 = $[87];
  }
  let t36;
  if ($[88] !== t35) {
    t36 = <Label htmlFor="faq-order">{t35}</Label>;
    $[88] = t35;
    $[89] = t36;
  } else {
    t36 = $[89];
  }
  let t37;
  if ($[90] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = event_3 => setSortOrder(event_3.target.value);
    $[90] = t37;
  } else {
    t37 = $[90];
  }
  let t38;
  if ($[91] !== sortOrder) {
    t38 = <Input id="faq-order" type="number" value={sortOrder} onChange={t37} />;
    $[91] = sortOrder;
    $[92] = t38;
  } else {
    t38 = $[92];
  }
  let t39;
  if ($[93] !== t36 || $[94] !== t38) {
    t39 = <div className="flex w-28 flex-col gap-2">{t36}{t38}</div>;
    $[93] = t36;
    $[94] = t38;
    $[95] = t39;
  } else {
    t39 = $[95];
  }
  let t40;
  if ($[96] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = event_4 => setPublished(event_4.target.checked);
    $[96] = t40;
  } else {
    t40 = $[96];
  }
  let t41;
  if ($[97] !== published) {
    t41 = <input type="checkbox" className="size-4" checked={published} onChange={t40} />;
    $[97] = published;
    $[98] = t41;
  } else {
    t41 = $[98];
  }
  let t42;
  if ($[99] !== t) {
    t42 = t("support.faq.field.published");
    $[99] = t;
    $[100] = t42;
  } else {
    t42 = $[100];
  }
  let t43;
  if ($[101] !== t42) {
    t43 = <span className="font-medium">{t42}</span>;
    $[101] = t42;
    $[102] = t43;
  } else {
    t43 = $[102];
  }
  let t44;
  if ($[103] !== t41 || $[104] !== t43) {
    t44 = <label className="flex items-center gap-2 pb-2 text-sm">{t41}{t43}</label>;
    $[103] = t41;
    $[104] = t43;
    $[105] = t44;
  } else {
    t44 = $[105];
  }
  let t45;
  if ($[106] !== t39 || $[107] !== t44) {
    t45 = <div className="flex items-end gap-4">{t39}{t44}</div>;
    $[106] = t39;
    $[107] = t44;
    $[108] = t45;
  } else {
    t45 = $[108];
  }
  let t46;
  if ($[109] !== actionError) {
    t46 = actionError && <p className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-sm text-destructive">{actionError.message}</p>;
    $[109] = actionError;
    $[110] = t46;
  } else {
    t46 = $[110];
  }
  let t47;
  if ($[111] !== t11 || $[112] !== t24 || $[113] !== t29 || $[114] !== t34 || $[115] !== t45 || $[116] !== t46) {
    t47 = <div className="flex flex-col gap-4">{t11}{t24}{t29}{t34}{t45}{t46}</div>;
    $[111] = t11;
    $[112] = t24;
    $[113] = t29;
    $[114] = t34;
    $[115] = t45;
    $[116] = t46;
    $[117] = t47;
  } else {
    t47 = $[117];
  }
  let t48;
  if ($[118] !== t) {
    t48 = t("confirm.cancel");
    $[118] = t;
    $[119] = t48;
  } else {
    t48 = $[119];
  }
  let t49;
  if ($[120] !== busy || $[121] !== onClose || $[122] !== t48) {
    t49 = <Button variant="ghost" onClick={onClose} disabled={busy}>{t48}</Button>;
    $[120] = busy;
    $[121] = onClose;
    $[122] = t48;
    $[123] = t49;
  } else {
    t49 = $[123];
  }
  const t50 = busy || !valid;
  let t51;
  if ($[124] !== article || $[125] !== t) {
    t51 = article ? t("support.faq.save") : t("support.faq.create");
    $[124] = article;
    $[125] = t;
    $[126] = t51;
  } else {
    t51 = $[126];
  }
  let t52;
  if ($[127] !== submit || $[128] !== t50 || $[129] !== t51) {
    t52 = <Button onClick={submit} disabled={t50}>{t51}</Button>;
    $[127] = submit;
    $[128] = t50;
    $[129] = t51;
    $[130] = t52;
  } else {
    t52 = $[130];
  }
  let t53;
  if ($[131] !== t49 || $[132] !== t52) {
    t53 = <DialogFooter>{t49}{t52}</DialogFooter>;
    $[131] = t49;
    $[132] = t52;
    $[133] = t53;
  } else {
    t53 = $[133];
  }
  let t54;
  if ($[134] !== t47 || $[135] !== t5 || $[136] !== t53) {
    t54 = <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">{t5}{t47}{t53}</DialogContent>;
    $[134] = t47;
    $[135] = t5;
    $[136] = t53;
    $[137] = t54;
  } else {
    t54 = $[137];
  }
  let t55;
  if ($[138] !== t3 || $[139] !== t54) {
    t55 = <Dialog open={true} onOpenChange={t3}>{t54}</Dialog>;
    $[138] = t3;
    $[139] = t54;
    $[140] = t55;
  } else {
    t55 = $[140];
  }
  return t55;
}